/**
 * OAuth sign-in links (Google).
 *
 * FACEBOOK WAS REMOVED (2026-08-26, owner request) from both the sign-in and
 * sign-up screens. Only the UI is gone: `AUTH_PATHS.oauthFacebookStart` and the
 * backend's `/auth/oauth/facebook/*` routes are deliberately left in place, so
 * an account already linked through Facebook is not stranded and restoring the
 * button is a UI change rather than a re-integration. The `oauth.facebook`
 * copy key is likewise kept in all three locales.
 *
 * MECHANICS (unchanged, and load-bearing — see OAuthButtons.test.tsx):
 *   - Real `<a href>` full-page navigations to the backend's
 *     `/auth/oauth/<provider>/start?return_to=<path>`, which 302s to the
 *     provider. The state cookie set on that redirect is the CSRF defence, so
 *     the request has to come from a genuine user gesture on a same-origin
 *     anchor — not `window.location.assign` from a button handler. Keyboard
 *     Enter and "open in new tab" come free.
 *   - The href bakes in the API base URL: on QA/dev the API sits under `/api`,
 *     and a bare `/auth/oauth/...` is swallowed by the SPA history fallback and
 *     renders the 404 page instead of reaching the backend (SCRUM-122/123).
 *   - No inline handlers, no provider SDK scripts — the strict CSP forbids both.
 *
 * WHAT CHANGED: they were two 40px pill buttons — below the 44px tap floor, and
 * visually a second pair of CTAs competing with the real submit. They are now
 * one ruled block, in the same label grid as the slip above them: brand mark in
 * a fixed left gutter, label on the shared text column, a drawn rule between
 * the two rows. A wall of them reads as one system, which is exactly the point
 * of the label-grid raise, and neither row can be mistaken for the primary
 * action.
 *
 * The brand mark keeps its own colours. Google's mark is a trademark with a
 * mandated palette; recolouring them to `--g-*` would be both
 * wrong and less recognisable. This is the one sanctioned exception to the
 * no-hardcoded-colour rule in this feature, and it is confined to the single
 * inline `<svg>` literal below.
 *
 * `buildOAuthHref` and `GoogleIcon` are exported for the guest sign-up gate
 * (SCRUM-274), which must reach the same `/auth/oauth/google/start` the same
 * way (a real `<a href>`).
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { CSSProperties } from "react";

import { AUTH_PATHS, getApiClientConfig } from "@/lib/api-client";

export interface OAuthButtonsProps {
  /** The SPA path the user should land on after handoff. Defaults to `/chat`. */
  returnTo?: string;
  /** Disable the buttons (e.g. while the email/password form is submitting). */
  disabled?: boolean;
}

export function buildOAuthHref(startPath: string, returnTo: string): string {
  const params = new URLSearchParams({ return_to: returnTo });
  return getApiClientConfig().baseUrl + startPath + "?" + params.toString();
}

export const GoogleIcon = (): ReactElement => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="20"
    height="20"
    viewBox="0 0 48 48"
  >
    {/* Google "G" — flat-color variant. Each branded color rendered
        inline; CSP is fine because the SVG is inline and same-origin. */}
    <path
      fill="#FFC107"
      d="M43.6 20.5h-1.6V20H24v8h11.3c-1.7 4.6-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.8 1.1 8 3l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="m6.3 14.7 6.6 4.8A12 12 0 0 1 24 12c3 0 5.8 1.1 8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5.1A20 20 0 0 0 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.2-.1-2.4-.4-3.5z"
    />
  </svg>
);

export function OAuthButtons({
  returnTo = "/chat",
  disabled = false,
}: OAuthButtonsProps): ReactElement {
  const { t } = useTranslation("auth");

  /*
   * The design draws each provider as its OWN bordered row (#FFFDF8, 12px
   * radius, 16/22 padding, 16px/500) whose border goes #16453F on hover —
   * not a single grouped card with internal rules. Reproduced exactly; the
   * hrefs, testids, disabled semantics and CSP posture are untouched.
   *
   * `focus-visible` gets the same border treatment as hover PLUS a ring: hover
   * alone leaves keyboard users with no focus indication at all, and the
   * design has no keyboard story to copy.
   */
  const rowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 14,
    background: "#FFFDF8",
    border: "1px solid rgba(23,32,29,0.12)",
    borderRadius: 12,
    padding: "var(--gav2-oauth-py) 22px",
    fontSize: 16,
    fontWeight: 500,
    color: "#17201D",
    cursor: disabled ? "default" : "pointer",
    transition: "border-color 0.2s",
    ...(disabled ? { pointerEvents: "none" as const, opacity: 0.5 } : {}),
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: "var(--gav2-gap)" }}
    >
      <a
        href={buildOAuthHref(AUTH_PATHS.oauthGoogleStart, returnTo)}
        className="gav2-oauth"
        style={rowStyle}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        data-testid="oauth-google"
      >
        <span className="grid size-5 shrink-0 place-items-center">
          <GoogleIcon />
        </span>
        <span>{t("oauth.google")}</span>
      </a>
    </div>
  );
}

export default OAuthButtons;
